import { FC, useMemo, useState, useEffect } from 'react';
import { Grid, Box, Typography, Button, Tabs, Tab, Card, CardContent, useTheme } from '@mui/material';
import { ArrowBack, Description, AutoAwesome, Score, People, Subject, Assessment, Schedule, CheckCircle } from '@mui/icons-material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { useAcademicsAssessmentsContext } from '../../contexts/academicsAssessments.context';
import {
  getAcademicsAssessmentDetailsGridColumns,
  getAssessmentSubjectsGridColumns,
} from './academicsAssessments.helpers';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { IAssessmentSubject } from '../../../../../apis/academics/academicsAssessments.interfaces';

export enum EAssessmentDetailsStep {
  SUBJECTS = 'subjects',
  SCORES = 'scores',
  REPORT_CARDS = 'report_cards',
}

enum EAssessmentDetailsTab {
  SUBJECTS = 'subjects',
  REPORT_CARDS = 'report_cards',
}

interface AcademicsAssessmentDetailsViewProps {
  onStepChange?: (step: EAssessmentDetailsStep) => void;
  onEditAssessment?: () => void;
  onSelectedSubjectChange?: (subject: IAssessmentSubject | null) => void;
}

const AcademicsAssessmentDetailsView: FC<AcademicsAssessmentDetailsViewProps> = ({ onStepChange, onEditAssessment, onSelectedSubjectChange }) => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const { assessmentDetailsQuery, selectedAssessmentId, isLoading } =
    useAcademicsAssessmentsContext();

  const [currentStep, setCurrentStep] = useState<EAssessmentDetailsStep>(
    EAssessmentDetailsStep.SUBJECTS
  );
  const [currentTab, setCurrentTab] = useState<EAssessmentDetailsTab>(
    EAssessmentDetailsTab.SUBJECTS
  );
  const [selectedSubject, setSelectedSubject] = useState<IAssessmentSubject | null>(null);

  // Notify parent of step changes
  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  const assessmentDetails = assessmentDetailsQuery.data;

  const subjectsColumns = useMemo(
    () => getAssessmentSubjectsGridColumns(translateText),
    [translateText]
  );

  const studentDetailsColumns = useMemo(
    () => getAcademicsAssessmentDetailsGridColumns(translateText),
    [translateText]
  );

  const handleSubjectClick = (params: any) => {
    if (params?.row) {
      const subject = assessmentDetails?.subjects.find(
        (s) => s.courseId === params.row.courseId
      );
      if (subject) {
        setSelectedSubject(subject);
        setCurrentStep(EAssessmentDetailsStep.SCORES);
        onSelectedSubjectChange?.(subject);
      }
    }
  };

  const handleBackToSubjects = () => {
    setSelectedSubject(null);
    setCurrentStep(EAssessmentDetailsStep.SUBJECTS);
    onSelectedSubjectChange?.(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: EAssessmentDetailsTab) => {
    setCurrentTab(newValue);
    setCurrentStep(
      newValue === EAssessmentDetailsTab.SUBJECTS
        ? EAssessmentDetailsStep.SUBJECTS
        : EAssessmentDetailsStep.REPORT_CARDS
    );
    setSelectedSubject(null);
  };

  const handleGenerateReportCard = (studentId: string) => {
    // TODO: Implement report card generation
    console.log('Generate report card for student:', studentId);
  };


  if (!selectedAssessmentId) {
    return (
      <Grid
        container
        spacing={0}
        sx={{ height: '100%', p: 2 }}
        data-testid="academics-assessment-details-container"
      >
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
            }}
          >
            <Typography variant="h6" color="textSecondary">
              {translateText('academics.assessments.assessmentDetails')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (!assessmentDetails) {
    return (
      <Grid
        container
        spacing={0}
        sx={{ height: '100%', p: 2 }}
        data-testid="academics-assessment-details-container"
      >
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
            }}
          >
            <Typography variant="body1" color="textSecondary">
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  // Render subjects/report cards view (Step 1)
  if (currentStep === EAssessmentDetailsStep.SUBJECTS || currentStep === EAssessmentDetailsStep.REPORT_CARDS) {
    const subjectRows = (assessmentDetails?.subjects || []).map((subject) => ({
      id: subject.courseId,
      courseId: subject.courseId,
      courseName: subject.courseName,
      subjectCode: subject.subjectCode,
      subjectType: subject.subjectType,
      scheduledDuration: subject.scheduledDuration,
      scheduledDate: subject.scheduledDate,
      totalStudents: subject.totalStudents || subject.scores.length,
      averageScore: subject.averageScore,
      averagePercentage: subject.averagePercentage,
    }));

    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ height: '100%', p: { xs: 1, sm: 2 } }}
        data-testid="academics-assessment-details-container"
      >
        <Grid item xs={12} data-testid="assessment-summary-section">
          <Box sx={{ mb: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 2, fontWeight: 600 }}>
              {translateText('academics.assessments.summary')}
            </Typography>
            <Card
              elevation={0}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.light}08 0%, ${theme.palette.primary.light}03 100%)`,
                border: `1px solid ${theme.palette.primary.light}30`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}>
                      {translateText('common.title')}
                    </Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, color: theme.palette.primary.main }}>
                      {assessmentDetails.name || '-'}
                    </Typography>
                  </Grid>
                  {assessmentDetails.description && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 0.5 }}>
                        {translateText('common.description')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {assessmentDetails.description}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.info.light}15 0%, ${theme.palette.info.light}05 100%)`,
                    border: `1px solid ${theme.palette.info.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.info.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Score sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.info.main }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.columns.maxScore')}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.info.main }}>
                      {assessmentDetails.maxScore}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.success.light}15 0%, ${theme.palette.success.light}05 100%)`,
                    border: `1px solid ${theme.palette.success.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.success.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Assessment sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.success.main }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.averagePercentage')}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.success.main }}>
                      {assessmentDetails.averagePercentage?.toFixed(2) || '-'}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.light}05 100%)`,
                    border: `1px solid ${theme.palette.primary.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.primary.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <People sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.expectedStudents')}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.primary.main }}>
                      {assessmentDetails.totalStudents || '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.secondary.light}15 0%, ${theme.palette.secondary.light}05 100%)`,
                    border: `1px solid ${theme.palette.secondary.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.secondary.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Subject sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.secondary.main }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.totalSubjects') || 'Total Subjects'}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.secondary.main }}>
                      {assessmentDetails.subjects?.length || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {assessmentDetails.scheduledStartDate && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.warning.light}05 100%)`,
                      border: `1px solid ${theme.palette.warning.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.warning.light}30`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Schedule sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.warning.main }} />
                      </Box>
                      <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {translateText('academics.assessments.columns.scheduledStartDate') || 'Scheduled Start Date'}
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.warning.main }}>
                        {assessmentDetails.scheduledStartDate ? new Date(assessmentDetails.scheduledStartDate).toLocaleDateString() : '-'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {assessmentDetails.scheduledEndDate && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.warning.light}05 100%)`,
                      border: `1px solid ${theme.palette.warning.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.warning.light}30`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Schedule sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.warning.main }} />
                      </Box>
                      <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {translateText('academics.assessments.columns.scheduledEndDate') || 'Scheduled End Date'}
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.warning.main }}>
                        {assessmentDetails.scheduledEndDate ? new Date(assessmentDetails.scheduledEndDate).toLocaleDateString() : '-'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {assessmentDetails.remarks && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[50]} 100%)`,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 1 }}>
                        {translateText('academics.assessments.columns.remarks')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {assessmentDetails.remarks}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="assessment details tabs"
              data-testid="assessment-details-tabs"
            >
              <Tab
                label={translateText('academics.assessments.subjects') || 'Subjects'}
                value={EAssessmentDetailsTab.SUBJECTS}
                data-testid="subjects-tab"
              />
              <Tab
                label={translateText('academics.assessments.reportCards') || 'Report Cards'}
                value={EAssessmentDetailsTab.REPORT_CARDS}
                data-testid="report-cards-tab"
              />
            </Tabs>
          </Box>
        </Grid>

        {currentTab === EAssessmentDetailsTab.SUBJECTS && (
          <Grid
            item
            xs={12}
            data-testid="subjects-grid-container"
            sx={{
              height: { xs: 'calc(100vh - 400px)', sm: 'calc(100vh - 500px)' },
              overflow: 'auto',
            }}
          >
            <PaginatedGrid
              dataTestId="academics-assessment-subjects-grid"
              rows={subjectRows}
              columns={subjectsColumns}
              loading={isLoading}
              onRowClick={handleSubjectClick}
              getRowId={(row) => row.id.toString()}
              hideColumnsOnMobile={['subjectCode']}
            />
          </Grid>
        )}

        {currentTab === EAssessmentDetailsTab.REPORT_CARDS && (
          <Grid
            item
            xs={12}
            data-testid="report-cards-container"
            sx={{
              height: { xs: 'calc(100vh - 400px)', sm: 'calc(100vh - 500px)' },
              overflow: 'auto',
            }}
          >
            {!assessmentDetails.reportCards || assessmentDetails.reportCards.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px',
                  p: 4,
                }}
              >
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  {translateText('academics.assessments.noReportCards') || 'No report cards available'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesome />}
                  onClick={() => handleGenerateReportCard('all')}
                  sx={{ mt: 2 }}
                  data-testid="generate-all-report-cards-button"
                >
                  {translateText('academics.assessments.generateAllReportCards') || 'Generate All Report Cards'}
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {assessmentDetails.reportCards.map((reportCard, index) => (
                  <Grid item xs={12} sm={6} md={4} key={reportCard.studentDetails.id || `${reportCard.studentDetails.firstName}-${reportCard.studentDetails.lastName}-${index}`}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {`${reportCard.studentDetails.firstName} ${reportCard.studentDetails.lastName}`}
                        </Typography>
                        {reportCard.reportCard ? (
                          <Box>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              {reportCard.reportCard.documentName}
                            </Typography>
                            {reportCard.reportCard.fileUrl && (
                              <Button
                                size="small"
                                startIcon={<Description />}
                                href={reportCard.reportCard.fileUrl}
                                target="_blank"
                                sx={{ mt: 1 }}
                                data-testid={`view-report-card-button-${index}`}
                              >
                                {translateText('common.view') || 'View'}
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              {translateText('academics.assessments.reportCardNotGenerated') || 'Report card not generated'}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AutoAwesome />}
                              onClick={() => handleGenerateReportCard(reportCard.studentDetails.id || '')}
                              sx={{ mt: 1 }}
                              data-testid={`generate-report-card-button-${index}`}
                            >
                              {translateText('academics.assessments.generateReportCard') || 'Generate'}
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    );
  }

  // Render scores grid (Step 2)
  if (currentStep === EAssessmentDetailsStep.SCORES && selectedSubject) {
    const totalStudents = selectedSubject.scores.length;
    const passedStudents = selectedSubject.scores.filter(
      (s) => s.score >= assessmentDetails.maxScore * 0.5
    ).length;
    const failedStudents = totalStudents - passedStudents;
    const passingScore = Math.round(assessmentDetails.maxScore * 0.5);

    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ height: '100%', p: { xs: 1, sm: 2 } }}
        data-testid="academics-assessment-details-container"
      >
        <Grid item xs={12} data-testid="subject-summary-section">
          <Box sx={{ mb: { xs: 1, sm: 2 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToSubjects}
                sx={{
                  minHeight: { xs: '44px', sm: '36px' },
                  fontSize: { xs: '16px', sm: '14px' },
                }}
                data-testid="back-to-subjects-button"
              >
                {translateText('academics.assessments.backToSubjects') || 'Back to Subjects'}
              </Button>
            </Box>
            <Card
              elevation={0}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.light}08 0%, ${theme.palette.primary.light}03 100%)`,
                border: `1px solid ${theme.palette.primary.light}30`,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, color: theme.palette.primary.main }}>
                  {selectedSubject.courseName}
                  {selectedSubject.subjectCode && ` (${selectedSubject.subjectCode})`}
                </Typography>
              </CardContent>
            </Card>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.info.light}15 0%, ${theme.palette.info.light}05 100%)`,
                    border: `1px solid ${theme.palette.info.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.info.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Score sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.info.main }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.columns.maxScore')}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.info.main }}>
                      {assessmentDetails.maxScore}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {selectedSubject.scheduledDuration && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.warning.light}05 100%)`,
                      border: `1px solid ${theme.palette.warning.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.warning.light}30`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Schedule sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.warning.main }} />
                      </Box>
                      <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {translateText('academics.assessments.columns.duration') || 'Duration'}
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.warning.main }}>
                        {(() => {
                          const hours = Math.floor(selectedSubject.scheduledDuration / 60);
                          const minutes = selectedSubject.scheduledDuration % 60;
                          if (hours > 0) {
                            return `${hours}h ${minutes}m`;
                          }
                          return `${minutes}m`;
                        })()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.success.light}15 0%, ${theme.palette.success.light}05 100%)`,
                    border: `1px solid ${theme.palette.success.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.success.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Assessment sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.success.main }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.averagePercentage')}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.success.main }}>
                      {selectedSubject.averagePercentage?.toFixed(2) || '-'}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.light}05 100%)`,
                    border: `1px solid ${theme.palette.primary.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.primary.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <People sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.actualStudents')}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.primary.main }}>
                      {totalStudents}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.success.light}15 0%, ${theme.palette.success.light}05 100%)`,
                    border: `1px solid ${theme.palette.success.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.success.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <CheckCircle sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.success.main }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.passedStudents')}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.success.main }}>
                      {passedStudents}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.error.light}15 0%, ${theme.palette.error.light}05 100%)`,
                    border: `1px solid ${theme.palette.error.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.error.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.error.main, fontWeight: 'bold' }}>✗</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.failedStudents')}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.error.main }}>
                      {failedStudents}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.warning.light}05 100%)`,
                    border: `1px solid ${theme.palette.warning.light}40`,
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.palette.warning.light}30`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Score sx={{ fontSize: { xs: 20, sm: 24 }, color: theme.palette.warning.main }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {translateText('academics.assessments.passingScore')}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: theme.palette.warning.main }}>
                      {passingScore}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {selectedSubject.remarks && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[50]} 100%)`,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Typography variant="body2" color="textSecondary" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 1 }}>
                        {translateText('academics.assessments.columns.remarks')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {selectedSubject.remarks}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          data-testid="student-assessments-grid-container"
          sx={{
            height: { xs: 'calc(100vh - 300px)', sm: 'calc(100vh - 400px)' },
            overflow: 'auto',
          }}
        >
          <PaginatedGrid
            dataTestId="academics-assessment-details-grid"
            rows={selectedSubject.scores.map((item, index) => ({
              id: `${selectedSubject.courseId}-${index}`,
              studentName: `${item.studentDetails.firstName} ${item.studentDetails.lastName}`,
              obtainedScore: item.score,
              maxScore: assessmentDetails.maxScore,
              percentage: (item.score / assessmentDetails.maxScore) * 100,
              date:
                assessmentDetails.gradedDate ||
                assessmentDetails.conductedDate ||
                '-',
            }))}
            columns={studentDetailsColumns}
            loading={isLoading}
            getRowId={(row) => row.id}
            hideColumnsOnMobile={['date']}
          />
        </Grid>
      </Grid>
    );
  }

  return null;
};

export default AcademicsAssessmentDetailsView;
